<template>
  <div class="vch-sign-in">
    <div class="vch-sign-in-title">
      <h1 class="vch-sign-in__title heading-xl uppercase">
        {{ currentTitle }}
        <span class="heading-xl mb-xl sub-heading">{{ titleSecondary }}</span>
      </h1>
    </div>

    <div class="vch-grid">
      <div class="vch-col-12 vch-col-offset-md-4 vch-col-md-5">
        <p class="vch-sign-in__instructions">
          {{ formMandatoryInstruction }}
        </p>
        <slot name="form">Form Here</slot>
      </div>
    </div>
  </div>
</template>
<script>
import { parseSitecoreForm, bindFormField } from 'Utils/form';
import { mixinFormRecaptcha } from 'Mixins/formRecaptcha/formRecaptcha';
import { pushEvent } from 'Utils/datalayer';

export default {
  name: 'vch-sign-in',
  mixins: [mixinFormRecaptcha],
  props: {
    formTitle: {
      type: String,
      required: true,
    },
    formMandatoryInstruction: {
      type: String,
    },
    titleSubheading: {
      type: String,
      required: true,
    },
    testHost: {
      type: String,
      required: false,
    },
    confirmButtonText: {
      type: String,
      required: true,
    },
    errorMessageFormIncomplete: {
      type: String,
      required: true,
    },
    errorMessageFormTerms: {
      type: String,
      required: true,
    },
    defaultErrorMessage: {
      type: String,
      required: false,
      default: 'An error occured while processing your request.',
    },
    successRedirectUrl: {
      type: String,
      required: false,
    },
    formDatalayer: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      currentTitle: this.formTitle,
      titleSecondary: this.titleSubheading,
      formSuccess: false,
      useEmailFlow: false,
      fields: [],
      formProperties: null,
      parser: new DOMParser(),
      errorWrapperNode: null,
      rememberMe: false,
      datalayerForm: null,
    };
  },
  computed: {
    formActionUrl() {
      return process.env.NODE_ENV === 'production'
        ? this.formProperties.formAction
        : `${this.testHost}${this.formProperties.formAction}`;
    },
  },
  mounted() {
    const formNode = this.$slots.form[0].children[0].children[0];
    if (!formNode?.elm) {
      return;
    }

    // Collect the base form properties and hijack form submit
    this.formProperties = parseSitecoreForm(formNode?.elm);
    formNode.elm.addEventListener('submit', this.onSubmit);

    // Create the error message wrapper
    this.errorWrapperNode = document.createElement('div');
    this.errorWrapperNode.setAttribute('role', 'status');
    this.errorWrapperNode.classList.add('vch-contact-form__errorSummary');
    const submitButton = formNode?.elm.querySelector('input[type="submit"]');
    submitButton.parentNode.insertBefore(this.errorWrapperNode, submitButton);

    // Traverse the form and collect all the inputs into a single list
    formNode.children
      .filter((node) => node.tag === 'div' || node.tag === 'input')
      .forEach((node) => {
        const foundFieldInfo = bindFormField(node);
        if (foundFieldInfo.nestedFieldInfo.length) {
          this.fields.push(...foundFieldInfo.nestedFieldInfo);
        }
        this.fields.push(foundFieldInfo);
      });

    // Function to get cookie value by name
    const getCookie = (name) => {
      const cookies = document.cookie
        .split('; ')
        .map((cookie) => cookie.split('='));

      for (const [cookieName, cookieValue] of cookies) {
        if (cookieName === name) {
          return decodeURIComponent(cookieValue);
        }
      }

      return null;
    };

    // Retrieve value from localStorage with a fallback function
    const getLocalStorageValue = (key, defaultValue = '') => {
      return decodeURIComponent(localStorage.getItem(key)) || defaultValue;
    };

    // Check if the 'remember_me' cookie exists
    const userDataCookie = getCookie('remember_me');
    if (userDataCookie) {
      // Set values using the refactored localStorage function
      document.querySelector('[type="email"]').value =
        getLocalStorageValue('vchUsername');
      document.querySelector('[type="password"]').value =
        getLocalStorageValue('vchPwd');

      // Use classList.toggle to add or remove the class
      document
        .querySelector('[type="email"]')
        .classList.toggle('-filled', true);
      document
        .querySelector('[type="password"]')
        .classList.toggle('-filled', true);

      // Use querySelector to find and check the checkbox
      document.querySelector('.remember-me').checked = true;
    }
    this.datalayerForm = JSON.parse(this.formDatalayer);
  },

  methods: {
    setErrorMessage(errorMessage) {
      this.errorWrapperNode.innerText = errorMessage;
    },
    validateFields() {
      const validationErrors = [];
      this.fields.forEach((field) => {
        const fieldErrors = field.validate();
        validationErrors.push(...fieldErrors);
      });

      if (validationErrors.length > 0) {
        // this.setErrorMessage(this.errorMessageFormIncomplete);
        return false;
      }
      return true;
    },
    async onSubmit(e) {
      e.preventDefault();
      if (!this.validateFields()) return;

      let submitButton = document.querySelector('.vch-form-submit');
      const rememberMeCheckbox = document.querySelector('.remember-me');

      if (rememberMeCheckbox.checked) {
        const now = new Date();
        const expires = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        document.cookie = `remember_me=true; expires=${expires.toUTCString()}; path=/`;
        const username = document.querySelector('[type="email"]').value;
        const password = document.querySelector('[type="password"]').value;

        // Encode the username and password before storing in the cookie
        const encodedUsername = encodeURIComponent(username);
        const encodedPassword = encodeURIComponent(password);

        // Combine encoded username and password into a single string
        localStorage.setItem('vchUsername', encodedUsername);
        localStorage.setItem('vchPwd', encodedPassword);
      } else {
        document.cookie =
          'remember_me=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
      this.peformRecaptcha()
        .then(async (canSubmit) => {
          if (canSubmit) {
            if (submitButton) {
              submitButton.setAttribute('disabled', 'disabled');
            }
            await fetch(this.formActionUrl, {
              method: 'POST',
              mode: process.env.NODE_ENV === 'production' ? 'cors' : 'no-cors',
              body: this.assembleFormData(),
            })
              .then((response) => {
                if (response.status >= 400 && response.status < 600) {
                  throw new Error(this.defaultErrorMessage);
                }
                return response.text();
              })
              .then((response) => {
                const htmlResponse = this.parser.parseFromString(
                  response,
                  'text/xml'
                );
                const errorWrapper = htmlResponse.querySelector(
                  '.validation-summary-errors'
                );
                if (errorWrapper) {
                  const serverErrorMsgs = errorWrapper.querySelectorAll('li');
                  const summaryErrors = Array.from(serverErrorMsgs).map(
                    (msg) => msg.textContent
                  );
                  this.setErrorMessage(summaryErrors[0]);
                  if (submitButton) {
                    submitButton.removeAttribute('disabled');
                  }
                } else {
                  this.formSuccess = true;
                  this.pushSubmitTags();
                  this.redirectPage();
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  });
                  if (submitButton) {
                    submitButton.removeAttribute('disabled');
                  }
                }
              })
              .catch((error) => {
                this.setErrorMessage(error.message);
                if (submitButton) {
                  submitButton.removeAttribute('disabled');
                }
              });
          }
        })
        .catch(() => {
          this.setErrorMessage(this.defaultErrorMessage);
          if (submitButton) {
            submitButton.setAttribute('disabled', 'disabled');
          }
        });
    },
    redirectPage() {
      window.location.href = this.successRedirectUrl;
    },

    assembleFormData() {
      const formData = new FormData();
      this.fields.forEach((field) => {
        if (field.inputElement) {
          formData.append(field.inputElement.name, field.inputElement.value);
        }
        field.hiddenFields.forEach((hiddenField) => {
          if (hiddenField.name) {
            formData.append(hiddenField.name, hiddenField.value);
          }
        });
      });
      return formData;
    },
    pushSubmitTags() {
      pushEvent({
        event: this.datalayerForm?.data?.eventProperties?.event,
        event_name: this.datalayerForm?.data?.eventProperties?.event_name,
        ecommerce: this.datalayerForm?.data?.eventProperties?.ecommerce,
        eventCategory: this.datalayerForm?.data?.eventProperties?.eventCategory,
        eventAction: this.datalayerForm?.data?.eventProperties?.eventAction,
        eventLabel: this.datalayerForm?.data?.eventProperties?.eventLabel,
        method: this.datalayerForm?.data?.eventProperties?.method,
        referral: this.datalayerForm?.data?.eventProperties?.referral,
      });
    },
  },
};
</script>

<style lang="scss" src="./sign-in.scss"></style>
